/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * Provides service for compare marketplace products
 *
 * @module enervee_utils
 */
ProductCompareService.$inject = [
    '$http',
    '$q',
    '$log',
    '$state',
    '$location',
    'AnalyticsService',
    'UtilService',
    'CookiesService',
    'SwiftypeService'
];
function ProductCompareService(
    $http,
    $q,
    $log,
    $state,
    $location,
    AnalyticsService,
    UtilService,
    CookiesService,
    SwiftypeService
) {
    const COOKIE_CURRENT_COMPARISON_PAGE = 'current_comparison_page_url';
    const COOKIE_COMPARISON_VIEWED_PRODUCT_ID = 'comparison_viewed_product_id';

    let _pub = {};
    let productPerCategoryTray = {};
    let activeSelection = [];
    let recommendedProductsUpdatedCallbacks = [];
    let recommendedProductDeletedCallbacks = [];

    // keeping compare products ids per category in cookies
    const COOKIE_COMPARISON_TRAYS = 'comparison_trays';
    const COOKIE_BACK_URL = 'comparison_back_url';
    const COOKIE_COMPARISON_PRODUCT = 'comparison_product';
    const savedTrays = CookiesService.get(COOKIE_COMPARISON_TRAYS);
    if (savedTrays !== undefined) {
        productPerCategoryTray = JSON.parse(savedTrays) || {};
    }
    let lastProductComparisonUrl = CookiesService.get(COOKIE_BACK_URL) || null;

    _pub.isComparisonEnabled = UtilService.isFeatureEnabled("product_comparison");
    _pub.onSelectionChanged = null;
    _pub.enableCallbacks = [];
    _pub.maxNumberCompareProduct =  UtilService.getSiteSetting('maximum_compare_products', 4);

    let isRecommendedTray = false;

    /**
     * Add product to selection
     * @param product - Object
     */
    function addProductToSelection(product) {
        const productInSelection = UtilService
            .reduceToProperty(activeSelection, 'id')
            .includes(product.id);
        if (activeSelection.length < _pub.maxNumberCompareProduct && !productInSelection) {
            activeSelection.push(product);
            return true;
        }
        return false;
    }

    /**
     * Delete product from tray
     * @param product - Object
     */
    function deleteProductFromSelection(product)  {
        for (let i = 0; i < activeSelection.length; i++) {
            if ( activeSelection[i].id === product.id) {
                activeSelection.splice(i, 1);
            }
        }
    }

    /**
     * save structure of compare products trays per category
     */
    function updateTrayCookie() {
        CookiesService.set(COOKIE_COMPARISON_TRAYS, productPerCategoryTray);
    }

    /**
     * call all callback functions to refresh tray
     * @param categoryId - int
     */
    function callRefreshTray(categoryId) {
        const enableCompare = productPerCategoryTray[categoryId].length < _pub.maxNumberCompareProduct;

        if (typeof _pub.onSelectionChanged === "function") {
            _pub.onSelectionChanged(productPerCategoryTray[categoryId]);
        }

        // enable/disable compare buttons
        _pub.enableCallbacks.forEach(callback => callback(enableCompare))
    }

    /**
     * check if compare tray not full
     */
    _pub.allowAddProduct = () => {
        return activeSelection.length < _pub.maxNumberCompareProduct;
    };

    /**
     * Add product to compare tray per category
     * @param product - Object
     */
    _pub.addProductToTray = product => {
        if (addProductToSelection(product) === false) {
            return;
        }

        // detail page recommended products
        if (isRecommendedTray === true) {
            return recommendedTrayChanged();
        }

        const categoryId = product.category_id;
        // first product for category
        if (!(categoryId in productPerCategoryTray)) {
            productPerCategoryTray[categoryId] = [];
        }

        productPerCategoryTray[categoryId].push(product.id);
        updateTrayCookie();
        callRefreshTray(categoryId);
    };

    /**
     * Delete product from compare tray and refresh tray when needed
     * @param product - Object
     * @param refresh - Boolean
     */
    _pub.deleteProductFromTray = (product, refresh=true) => {
        deleteProductFromSelection(product);

        // detail page recommended products
        if (isRecommendedTray === true) {
            return callRecommendedProductDeleted();
        }

        const categoryId = product.category_id;
        let products = productPerCategoryTray[categoryId] || [];
        for (let i = 0; i < products.length; i++) {
            if (products[i] === product.id) {
                products.splice(i, 1);
            }
        }
        updateTrayCookie();
        if (refresh === true) {
            callRefreshTray(categoryId);
        }
    };

    /**
     * Clear compare tray for category
     * @param categoryId
     */
    _pub.clearCategoryTray = categoryId =>  {
        productPerCategoryTray[categoryId] = [];
    };

    /**
     * Init current tray selection for category
     * @param category - Object
     */
    _pub.initTrayProducts = category =>  {
        isRecommendedTray = false;
        const categoryId = category.id;
        productPerCategoryTray[categoryId] = productPerCategoryTray[categoryId] || [];
        activeSelection = [];

        _pub.enableCallbacks = [];
        if (productPerCategoryTray[categoryId].length > 0) {
            initTrayProductsSwiftype(category, productPerCategoryTray[categoryId]);
        }
    };

    /**
     * Get tray products from swiftype
     * @param category - Object
     * @param productIds - Array
     */
    function initTrayProductsSwiftype(category, productIds)  {
        let categoryCodeToIds = {};
        categoryCodeToIds[ category.code ] = productIds;

        SwiftypeService.getProducts(categoryCodeToIds, false).then(function (products) {
            activeSelection = UtilService.objectValues(products);
            callRefreshTray(category.id);
        });
    }

    /**
     * Get product list from comparison tray
     */
    _pub.getTrayProducts = () =>  activeSelection;

    /**
     * Check if product in comparison tray
     * @param product - Object
     */
    _pub.isProductInTray = product => {
        if (isRecommendedTray === true) {
            return activeSelection.indexOf(product) > -1;
        }

        let products = productPerCategoryTray[product.category_id] || [];
        return products.indexOf(product.id) > -1;
    };

    /**
     * Check cookies for recommended products tray selection
     * for given product
     * @param id - int id of current product
     * @return list of product ids in tray
     */
    _pub.getRecommendedTrayProductsFromCookie = (id) => {
        let productTraySelectionIds = [];
        const savedRecommendedTray = CookiesService.get(COOKIE_COMPARISON_PRODUCT);

        if (savedRecommendedTray !== undefined) {
            productTraySelectionIds = JSON.parse(savedRecommendedTray) || [];
        }
        //check if cookie has selection for this product
        if (productTraySelectionIds.indexOf(id) < 0) {
            productTraySelectionIds = [];
        }
        return productTraySelectionIds;
    };

    /**
     * Init recommended products tray selection
     * @param products - Array
     * @return current recommended products tray
     */
    _pub.initRecommendedTrayProducts = products =>  {
        isRecommendedTray = true;
        activeSelection = products || [];
        recommendedProductsUpdatedCallbacks = [];
        recommendedProductDeletedCallbacks = [];
        if (_pub.isTrayEmpty() === true){
            _pub.enableCallbacks = [];
        }
        callRefreshTrayRecommended();

        return activeSelection;
    };

    /**
     * checking if tray empty or not
     */
    _pub.isTrayEmpty = () => (activeSelection.length === 0) ;


    /**
     * Registers the given callback, so it will be called after the recommended product is updated.
     *
     * @param callback
     */
    _pub.onRecommendedProductsUpdated = callback => {
        recommendedProductsUpdatedCallbacks.push(callback);
    };

    /**
     * Registers the given callback, so it will be called after the recommended product is deleted.
     *
     * @param callback
     */
    _pub.onRecommendedProductDeleted = callback => {
        recommendedProductDeletedCallbacks.push(callback);
    };

    /**
     * Triggers the callbacks registered to be called after the recommended products are updated
     * @param recommendedProducts
     */
    _pub.triggerRecommendedProductsUpdated = (recommendedProducts) => {
        recommendedProductsUpdatedCallbacks.forEach(function (callback) {
            callback(recommendedProducts);
        });
        recommendedProductsUpdatedCallbacks = [];
    };

    /**
     * Update cookie and refresh try
     */
    function recommendedTrayChanged() {
        CookiesService.set(COOKIE_COMPARISON_PRODUCT, activeSelection.map(({ id }) => id));
        callRefreshTrayRecommended();
    }

    /**
     * Triggers the callbacks registered to be called after a recommended product is deleted
     * @param productId - int
     */
    function callRecommendedProductDeleted(productId) {
        recommendedProductDeletedCallbacks.forEach(function (callback) {
            callback(productId);
        });
        recommendedTrayChanged();
    }

    /**
     * call all callback functions for tray on details page
     */
    function callRefreshTrayRecommended() {
        let enableCompare = activeSelection.length < _pub.maxNumberCompareProduct;

        if (typeof _pub.onSelectionChanged === "function") {
            _pub.onSelectionChanged(activeSelection);
        }

        // enable/disable compare buttons
        _pub.enableCallbacks.forEach(callback => callback(enableCompare));
    }

    /**
     * save url of last comparison page
     * @param url
     */
    _pub.setLastComparisonUrl = url =>  {
        lastProductComparisonUrl = url;
        CookiesService.set(COOKIE_BACK_URL, url);
    };

    /**
     * get url of last comparison page
     */
    _pub.getLastComparisonUrl = () => lastProductComparisonUrl;

    /**
     * Saves the url of the current comparison page along with the id of the product clicked on.
     * @param url <str> comparison url
     * @param productId <int> the product id of the product which will be redirected to from the comparison page
     */
    _pub.setCurrentComparisonPageUrl = (url, productId) =>  {
        CookiesService.set(COOKIE_CURRENT_COMPARISON_PAGE, url);
        CookiesService.set(COOKIE_COMPARISON_VIEWED_PRODUCT_ID, productId);
    };

    /**
     * Gets the url of current comparison page, if the product id is not the same as the one clicked on in the comparison
     * page, we need to cleanup the current comparison page url and product id.
     */
    _pub.getCurrentComparisonPageUrl = (productId) => {
        const savedProductId = CookiesService.get(COOKIE_COMPARISON_VIEWED_PRODUCT_ID);
        if (productId === parseInt(savedProductId)) {
            return CookiesService.get(COOKIE_CURRENT_COMPARISON_PAGE);
        } else {
            _pub.removeCurrentComparisonPageUrl();
        }
    };

    /**
     * Removes the url of the current comparison page along with the viewed product id from the comparison page
     */
    _pub.removeCurrentComparisonPageUrl = () =>  {
        CookiesService.remove(COOKIE_CURRENT_COMPARISON_PAGE);
        CookiesService.remove(COOKIE_COMPARISON_VIEWED_PRODUCT_ID);
    };

    /**
     * Removes the url for go back from compare page
     */
    _pub.removeComparisonBackUrl = () =>  {
        CookiesService.remove(COOKIE_BACK_URL);
    };

    /**
     * Reports event and then redirect to compare page.  Also limits number of
     * products to compare to the max limit from the Setting Service
     *
     * @param {Array} products - Products to be compared.
     * @param {Object} additionalParams - Additional params to be added to the $state.go call.
     */
    _pub.goToComparePage = (products, additionalParams) => {
        _pub.setLastComparisonUrl($location.url());
        const productsToCompare = products.slice(0, _pub.maxNumberCompareProduct);
        AnalyticsService.reportEvent('Compare Started', {}, productsToCompare);
        const productIds = productsToCompare.map(product => product.id);

        $state.go('compare', {
            pid: productIds,
            ...additionalParams
        });
    };

    return _pub;
}

module.exports = ProductCompareService;
